import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"

export default class extends Controller {
  static values = {
    delay: { type: Number, default: 300 }
  }

  static targets = ["form", "input", "select", "minPrice", "maxPrice"]

  initialize() {
    this.debouncedSubmit = debounce(this._submit.bind(this), this.delayValue)
    this.debouncedPriceChange = debounce(this._handlePriceChange.bind(this), this.delayValue)
  }

  connect() {
    // Listen for custom event from clear filters
    document.addEventListener('filters:clear', this.handleClear.bind(this))
    
    // Listen for turbo:before-fetch events
    document.addEventListener('turbo:before-fetch-request', this.beforeFetchRequest.bind(this))
  }

  disconnect() {
    this.debouncedSubmit.cancel()
    if (this.debouncedPriceChange) {
      this.debouncedPriceChange.cancel()
    }
    document.removeEventListener('filters:clear', this.handleClear.bind(this))
    document.removeEventListener('turbo:before-fetch-request', this.beforeFetchRequest.bind(this))
  }

  beforeFetchRequest(event) {
    // Preserve focus element for restoration after morph
    if (document.activeElement) {
      const focusId = document.activeElement.id
      const focusValue = document.activeElement.value
      const focusPosition = document.activeElement.selectionStart
      
      // Store focus information for later restoration
      this.lastFocusInfo = { 
        id: focusId, 
        value: focusValue, 
        position: focusPosition 
      }
      
      // Dispatch event so that Turbo knows about this state
      document.dispatchEvent(new CustomEvent('turbo:morph-focus', { 
        detail: this.lastFocusInfo 
      }))
    }
  }

  debouncePriceChange(event) {
    this.debouncedPriceChange(event)
  }

  submit(event) {
    event.preventDefault()
    this.debouncedSubmit(event)
  }

  change(event) {
    this._submit(event)
  }

  _handlePriceChange(event) {
    this._submit(event)
  }

  _submit(event) {
    // Use Turbo Visit for seamless page transitions with morphing
    const form = this.hasFormTarget ? this.formTarget : event.target.closest('form')
    if (form) {
      const formData = new FormData(form)
      const searchParams = new URLSearchParams()
      
      // Only add non-empty parameters to the URL
      for (const [key, value] of formData.entries()) {
        if (value.trim() !== '') {
          searchParams.append(key, value)
        }
      }

      // Focus information to restore after the page load
      let focusInfo = null
      if (document.activeElement) {
        focusInfo = {
          id: document.activeElement.id,
          position: document.activeElement.selectionStart
        }
      }

      // Update the clear button state
      this.updateClearButtonState(searchParams.toString() !== '')

      // Navigate with Turbo for SPA-like experience
      Turbo.visit(`${form.action}?${searchParams.toString()}`, { 
        action: 'replace',
        frame: 'products_list'
      })

      // Dispatch event for filter changes
      document.dispatchEvent(new CustomEvent('filter-form:change'))
    }
  }

  clear(event) {
    event.preventDefault()
    
    // Reset all select fields
    if (this.hasSelectTarget) {
      this.selectTargets.forEach(select => {
        select.value = ''
      })
    }

    // Reset input fields
    if (this.hasInputTarget) {
      this.inputTargets.forEach(input => {
        input.value = ''
      })
    }

    // Reset price range fields
    if (this.hasMinPriceTarget) {
      this.minPriceTarget.value = ''
    }
    
    if (this.hasMaxPriceTarget) {
      this.maxPriceTarget.value = ''
    }

    // Dispatch a filters:clear event
    document.dispatchEvent(new CustomEvent('filters:clear'))

    // Submit the form with cleared values
    const form = this.hasFormTarget ? this.formTarget : event.target.closest('form')
    if (form) {
      // Make the request with empty params
      const url = new URL(window.location.href)
      const params = new URLSearchParams()
      
      // Preserve sort parameters if needed
      const currentUrl = new URL(window.location.href)
      const currentParams = new URLSearchParams(currentUrl.search)
      const sortParams = ['latest', 'price_asc', 'price_desc', 'name_asc', 'name_desc']
      
      sortParams.forEach(param => {
        if (currentParams.has(param)) {
          params.set(param, currentParams.get(param))
        }
      })
      
      // Make the request
      const finalUrl = `${url.pathname}?${params.toString()}`
      window.history.replaceState({}, '', finalUrl)
      
      // Update the clear button state to disabled
      this.updateClearButtonState(false)
      
      // Dispatch event for filter changes
      window.dispatchEvent(new CustomEvent('filters:changed'))
      
      Turbo.visit(finalUrl, { 
        action: 'replace', 
        frame: 'products_list' 
      })
    }
  }

  handleClear() {
    // Reset all select fields
    if (this.hasSelectTarget) {
      this.selectTargets.forEach(select => {
        select.value = ''
      })
    }

    // Reset input fields
    if (this.hasInputTarget) {
      this.inputTargets.forEach(input => {
        input.value = ''
      })
    }

    // Reset price range fields
    if (this.hasMinPriceTarget) {
      this.minPriceTarget.value = ''
    }
    
    if (this.hasMaxPriceTarget) {
      this.maxPriceTarget.value = ''
    }
  }

  // Add a new method to update the clear button state
  updateClearButtonState(hasFilters) {
    const clearButton = document.querySelector('[data-action="click->filter-form#clear"]')
    const disabledClearButton = document.querySelector('[onclick="return false;"]')
    
    if (hasFilters) {
      if (disabledClearButton) {
        // Enable the clear button
        disabledClearButton.classList.remove('text-gray-400', 'bg-white/50', 'cursor-not-allowed', 'dark:bg-gray-800/50', 'dark:text-gray-500')
        disabledClearButton.classList.add('text-white', 'bg-red-600', 'hover:bg-red-700', 'dark:bg-red-700', 'dark:text-white', 'dark:hover:bg-red-800', 'shadow-lg', 'transition-all', 'duration-300', 'hover:shadow-red-500/10', 'hover:shadow-xl')
        disabledClearButton.removeAttribute('disabled')
        disabledClearButton.removeAttribute('onclick')
        disabledClearButton.setAttribute('data-action', 'click->filter-form#clear')
      }
    } else {
      if (clearButton) {
        // Disable the clear button
        clearButton.classList.remove('text-white', 'bg-red-600', 'hover:bg-red-700', 'dark:bg-red-700', 'dark:text-white', 'dark:hover:bg-red-800', 'shadow-lg', 'transition-all', 'duration-300', 'hover:shadow-red-500/10', 'hover:shadow-xl')
        clearButton.classList.add('text-gray-400', 'bg-white/50', 'cursor-not-allowed', 'dark:bg-gray-800/50', 'dark:text-gray-500')
        clearButton.setAttribute('disabled', true)
        clearButton.setAttribute('onclick', 'return false;')
        clearButton.removeAttribute('data-action')
      }
    }
  }
} 
